import React, { useEffect, useRef, useState } from 'react';
import './HomegallerySection.css';
import health1 from '../Assets/gallery/health1.webp';
import health2 from '../Assets/gallery/health2.webp';
import health3 from '../Assets/gallery/health3.webp';
import health4 from '../Assets/gallery/health4.webp';
import const1 from '../Assets/gallery/const1.webp';
import const2 from '../Assets/gallery/const2.webp';
import const3 from '../Assets/gallery/const3.webp';
import const4 from '../Assets/gallery/const4.webp';
import it1 from '../Assets/gallery/it1.webp';
import it2 from '../Assets/gallery/it2.webp';
import it3 from '../Assets/gallery/it3.webp';

const imageData = [
  { id: 1, src: health1, alt: 'Mountain Landscape' },
  { id: 2, src: health2, alt: 'Forest Path' },
  { id: 3, src: health3, alt: 'Lake Reflection' },
  { id: 4, src: health4, alt: 'Ocean Sunset' },
  { id: 5, src: const1, alt: 'Modern Construction' },
  { id: 6, src: const2, alt: 'City Lights' },
  { id: 7, src: it1, alt: 'Technology Hub' },
  { id: 8, src: it2, alt: 'Creative Workspace' },
  { id: 9, src: it3, alt: 'Code & Creativity' },
  { id: 10, src: const3, alt: 'Design Vision' },
  { id: 11, src: const4, alt: 'Urban Giants' },
];

const HomegallerySection = () => {
  const [currentIndex, setCurrentIndex] = useState(3);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [popupIndex, setPopupIndex] = useState(null);
  const autoplayRef = useRef(null);
  const containerRef = useRef(null);

  const navigate = (direction) => {
    if (isAnimating) return;
    const nextIndex = (currentIndex + direction + imageData.length) % imageData.length;
    setCurrentIndex(nextIndex);
    stopAutoplay();
  };

  const goToIndex = (index) => {
    if (isAnimating || index === currentIndex) return;
    setCurrentIndex(index);
    stopAutoplay();
  };

  const startAutoplay = () => {
    if (!autoplayRef.current) {
      autoplayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % imageData.length);
      }, 4000);
      setIsPlaying(true);
    }
  };

  const stopAutoplay = () => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    startAutoplay();
    containerRef.current?.focus();
    return () => clearInterval(autoplayRef.current);
  }, []);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 600);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  const handleKeyDown = (e) => {
    if (popupIndex !== null) {
      if (e.key === 'ArrowLeft') showPrevPopup();
      if (e.key === 'ArrowRight') showNextPopup();
      if (e.key === 'Escape') closePopup();
    } else {
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    }
  };

  const touch = useRef({ startX: 0, endX: 0 });

  const handleTouchStart = (e) => {
    touch.current.startX = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touch.current.endX = e.changedTouches[0].screenX;
    const delta = touch.current.startX - touch.current.endX;
    if (Math.abs(delta) > 30) {
      navigate(delta > 0 ? 1 : -1);
    }
  };

  const openPopup = (index) => {
    setPopupIndex(index);
    stopAutoplay();
  };

  const closePopup = () => {
    setPopupIndex(null);
    startAutoplay();
  };

  const showPrevPopup = () => {
    setPopupIndex((prev) => (prev - 1 + imageData.length) % imageData.length);
  };

  const showNextPopup = () => {
    setPopupIndex((prev) => (prev + 1) % imageData.length);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  return (
    <section className="cfw-section">
      <div className="cfw-wrapper">
        <div className="cfw-info">
          <h2>A Journey of Innovation, <br />Captured in Every Frame.</h2>
          <p>A Visual Symphony of Our Journey from 2007 to Now.</p>
        </div>

        <div
          className="cfw-container"
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className={`cfw-coverflow ${isAnimating ? 'animating' : ''}`}>
            {imageData.map((img, index) => {
              let offset = index - currentIndex;
              if (offset > imageData.length / 2) offset -= imageData.length;
              if (offset < -imageData.length / 2) offset += imageData.length;

              const absOffset = Math.abs(offset);
              const sign = Math.sign(offset);
              const translateX = offset * 220;
              const translateZ = -absOffset * 200;
              const rotateY = -sign * Math.min(absOffset * 60, 60);
              const opacity = absOffset > 3 ? 0 : 1 - absOffset * 0.2;
              const scale = 1 - absOffset * 0.1;
              const zIndex = 100 - absOffset;

              return (
                <div
                  key={index}
                  className={`cfw-item ${index === currentIndex ? 'active' : ''}`}
                  style={{
                    transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
                    opacity,
                    zIndex,
                  }}
                  onClick={() => openPopup(index)}
                >
                  <div className="cfw-cover">
                    <img src={img.src} alt={img.alt} loading="lazy" />
                  </div>
                  <div className="cfw-reflection" style={{ backgroundImage: `url(${img.src})` }} />
                </div>
              );
            })}
          </div>

          <button className="cfw-nav-btn cfw-prev" onClick={() => navigate(-1)}>‹</button>
          <button className="cfw-nav-btn cfw-next" onClick={() => navigate(1)}>›</button>

          <div className="cfw-dots">
            {imageData.map((_, index) => (
              <div
                key={index}
                className={`cfw-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToIndex(index)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ---------- Popup ---------- */}
      {popupIndex !== null && (
        <div className="cfw-popup" onClick={closePopup}>
          <div className="cfw-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="cfw-close-btn" onClick={closePopup}>×</button>
            <img src={imageData[popupIndex].src} alt={imageData[popupIndex].alt} />
            <button className="cfw-popup-nav prev" onClick={showPrevPopup}>‹</button>
            <button className="cfw-popup-nav next" onClick={showNextPopup}>›</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default HomegallerySection;
